import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useCrmMeta } from "../../hooks/useCrmMeta";
import ActivityLogModal from "../../components/crm/ActivityLogModal";
import { Phone, Mail, Video, StickyNote, FileText, ClipboardCheck } from "lucide-react";

const s = (v, fallback = "—") => {
    if (v == null || v === "") return fallback;
    if (typeof v === "string" || typeof v === "number") return v;
    return fallback;
};

const TYPE_ICON = { Call: Phone, Email: Mail, Meeting: Video, Note: StickyNote, Task: ClipboardCheck };

export default function MyActivity() {
    const { meta } = useCrmMeta();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);

    const [type, setType] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [openOnly, setOpenOnly] = useState(false);

    const [expanded, setExpanded] = useState(() => new Set());

    const [logOpen, setLogOpen] = useState(false);
    const [logType, setLogType] = useState("Call");

    const load = async () => {
        setLoading(true);
        setErrored(false);
        try {
            const { data } = await api.get("/crm/activities", {
                params: {
                    type: type || undefined,
                    dateFrom: dateFrom || undefined,
                    dateTo: dateTo || undefined,
                    openOnly: openOnly || undefined,
                },
            });
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            setErrored(true);
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, dateFrom, dateTo, openOnly]);

    const toggle = (id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const openLog = (t) => {
        setLogType(t);
        setLogOpen(true);
    };

    const summary = useMemo(() => {
        const total = rows.length;
        const openTasks = rows.filter((r) => r.is_task && !r.completed).length;
        return { total, openTasks };
    }, [rows]);

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="crm-my-activity-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">CRM</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">My Activity</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {summary.total} logged · {summary.openTasks} open task{summary.openTasks === 1 ? "" : "s"}
                    </p>
                </div>

                <div className="flex flex-wrap gap-2">
                    <button onClick={() => openLog("Call")} className="h-9 inline-flex items-center gap-1 px-3 rounded-md border border-border text-sm hover:bg-secondary" data-testid="log-call-button">
                        <Phone className="w-4 h-4" /> Log Call
                    </button>
                    <button onClick={() => openLog("Email")} className="h-9 inline-flex items-center gap-1 px-3 rounded-md border border-border text-sm hover:bg-secondary" data-testid="log-email-button">
                        <Mail className="w-4 h-4" /> Log Email
                    </button>
                    <button onClick={() => openLog("Meeting")} className="h-9 inline-flex items-center gap-1 px-3 rounded-md border border-border text-sm hover:bg-secondary" data-testid="log-meeting-button">
                        <Video className="w-4 h-4" /> Log Meeting
                    </button>
                </div>
            </div>

            <div className="sc-card p-3 flex flex-col md:flex-row md:items-center gap-3">
                <select className="sc-input md:w-48" value={type} onChange={(e) => setType(e.target.value)}>
                    <option value="">All types</option>
                    {(meta.logged_types || ["Call", "Email", "Meeting", "Note"]).map((t) => (
                        <option key={t} value={t}>{t}</option>
                    ))}
                </select>
                <input type="date" className="sc-input md:w-44" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                <span className="text-xs text-muted-foreground hidden md:inline">to</span>
                <input type="date" className="sc-input md:w-44" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
                    Open tasks only
                </label>
            </div>

            <div className="sc-card">
                {loading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
                {!loading && errored && <div className="p-8 text-center text-sm text-muted-foreground">Couldn't load your activity.</div>}
                {!loading && !errored && rows.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No activity found for this filter.</div>}

                {!loading && !errored && rows.length > 0 && (
                    <ul className="divide-y divide-border">
                        {rows.map((a) => {
                            const Icon = TYPE_ICON[a.type] || FileText;
                            const isOpen = expanded.has(a.id);
                            const hasPresented = (a.presented_quotation_nos || []).length > 0 || (a.presented_items || []).length > 0 || a.presented_url || a.presented_notes;

                            return (
                                <li key={a.id} className="p-3" data-testid={`my-activity-row-${a.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-2 min-w-0">
                                            <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium truncate">{s(a.subject)}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {s(a.type)} · {s(a.related_type, "")}{a.related_label ? `: ${s(a.related_label)}` : ""}
                                                </div>
                                                <div className="text-xs text-muted-foreground font-mono">
                                                    {a.occurred_at ? new Date(a.occurred_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—"}
                                                </div>
                                            </div>
                                        </div>
                                        {a.outcome && <span className="sc-pill bg-secondary border border-border shrink-0">{s(a.outcome)}</span>}
                                    </div>

                                    {(a.pitch || hasPresented) && (
                                        <button onClick={() => toggle(a.id)} className="text-xs text-muted-foreground underline underline-offset-2 mt-2">
                                            {isOpen ? "Hide details" : "Show pitch / presented"}
                                        </button>
                                    )}

                                    {isOpen && (
                                        <div className="mt-2 text-xs space-y-2 bg-secondary/30 rounded-md p-3">
                                            {a.pitch && (
                                                <div>
                                                    <div className="font-medium text-muted-foreground uppercase tracking-wider text-[10px] mb-1">Pitch</div>
                                                    <div className="whitespace-pre-wrap">{s(a.pitch)}</div>
                                                </div>
                                            )}
                                            {(a.presented_quotation_nos || []).length > 0 && (
                                                <div className="flex flex-wrap gap-1">
                                                    {a.presented_quotation_nos.map((no, i) => (
                                                        <span key={i} className="sc-pill bg-background border border-border font-mono">{s(no)}</span>
                                                    ))}
                                                </div>
                                            )}
                                            {(a.presented_items || []).length > 0 && (
                                                <div className="flex flex-wrap gap-1">
                                                    {a.presented_items.map((it, i) => (
                                                        <span key={i} className="sc-pill bg-background border border-border">
                                                            <span className="font-mono">{s(it?.item_code)}</span>{it?.item_name ? ` · ${s(it.item_name)}` : ""}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            {a.presented_url && (
                                                <a href={a.presented_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all block">{s(a.presented_url)}</a>
                                            )}
                                            {a.presented_notes && <div className="whitespace-pre-wrap">{s(a.presented_notes)}</div>}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {logOpen && (
                <ActivityLogModal
                    open={logOpen}
                    defaultType={logType}
                    onClose={() => setLogOpen(false)}
                    onSaved={load}
                />
            )}
        </div>
    );
}
